import { ViewportScroller } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FieldTypeConfig, FormlyFieldConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { FormlyFieldProps } from '@ngx-formly/material/form-field';
import { cloneDeep, forEach, get, replace } from 'lodash';
import { Subject, takeUntil } from 'rxjs';

import { DynamicFieldAction } from '../dynamic-field-type/dynamic-field-type.component';

interface InputProps extends FormlyFieldProps {
  tooltip: { text: string };
  header: string;
  cardViewDisabled?: boolean;
  readonly: boolean;
  print?: boolean;
  origin?: string;
}

@Component({
    selector: 'lockton-apps-address-type',
    templateUrl: './address-type.component.html',
    styleUrls: ['./address-type.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AddressTypeComponent
  extends FieldType<FieldTypeConfig<InputProps>>
  implements OnInit, AfterViewInit, OnDestroy
{
  @HostBinding('attr.id') get attrId() {
    return this.field.key;
  }

  readonly destroyer$: Subject<void> = new Subject();
  readonly internalForm: FormGroup = new FormGroup({});
  readonly fields: FormlyFieldConfig[] = [
    {
      type: 'input',
      key: 'street',
      props: {
        label: $localize`Street`,
      },
    },
    {
      type: 'input',
      key: 'cityTown',
      props: {
        label: $localize`City/Town`,
      },
    },
    {
      type: 'input',
      key: 'postcode',
      props: {
        label: $localize`Postcode`,
      },
    },
    {
      type: 'input',
      key: 'country',
      props: {
        label: $localize`Country`,
      },
    },
  ];
  isAddressSet!: boolean;
  showManualAddress!: boolean;

  constructor(
    private readonly viewportScroller: ViewportScroller,
    private readonly cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.enhanceFields(this.fields);
  }

  ngAfterViewInit(): void {
    this.initializeForm();

    this.isAddressSet = this.internalForm.valid;
    this.showManualAddress =
      (this.internalForm.invalid && this.field.defaultValue) ||
      this.props.cardViewDisabled;

    this.internalForm.valueChanges
      .pipe(takeUntil(this.destroyer$))
      .subscribe(() => {
        this.formControl.markAsDirty();
      });

    this.listenToExternalChanges();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.destroyer$.next();
    this.destroyer$.complete();
  }

  toggleManualAddress(): void {
    this.showManualAddress = !this.showManualAddress;
  }

  edit(): void {
    this.isAddressSet = false;
    this.showManualAddress = true;
    this.scrollToForm();
  }

  delete(): void {
    this.isAddressSet = false;
    this.showManualAddress = false;
    this.internalForm.reset();
  }

  onAddressSelected(rawAddress: any): void {
    this.internalForm.setValue({
      street: replace(`${rawAddress.Line1}, ${rawAddress.Line2}`, /, $/, ''),
      cityTown: rawAddress.City,
      country: rawAddress.CountryName,
      postcode: rawAddress.PostalCode,
    });

    this.showManualAddress = true;
    this.onModelChange(this.internalForm.value);
  }

  onModelChange(data: any): void {
    this.formControl.patchValue(data);
    this.form.updateValueAndValidity();
  }

  private initializeForm(): void {
    const initialValue: any =
      cloneDeep(get(this.field.model, this.field.key || '')) ||
      get(
        this.field.defaultValue,
        this.field.key as string,
        this.field.defaultValue
      );

    this.formControl.setValidators(() =>
      this.internalForm.invalid ? { invalidAncestors: true } : null
    );

    this.internalForm.patchValue(initialValue, { emitEvent: false });

    this.cdr.markForCheck();

    if (this.options.formState.isMulti) {
      this.linkControl();
    }
  }

  private enhanceFields(fields: FormlyFieldConfig[]): void {
    forEach(fields, (field: FormlyFieldConfig) => {
      field.id = `${this.field.id}-${field.key}`;
      (field.props as FormlyFieldProps).required = this.props.required;
      field.className = 'form__field';
    });
  }

  private linkControl(): void {
    (this.form as FormGroup).setControl(
      this.field.key as string,
      this.internalForm,
      { emitEvent: false }
    );
  }

  private listenToExternalChanges(): void {
    if (this.options.formState.externalAction$) {
      this.options.formState
        .externalAction$()
        .pipe(takeUntil(this.destroyer$))
        .subscribe(this.handleExternalAction.bind(this));
    }
    if (this.options.formState.validityCheck$) {
      this.options.formState.validityCheck$
        .pipe(takeUntil(this.destroyer$))
        .subscribe(this.onGlobalValidityCheck.bind(this));
    }
  }

  private handleExternalAction(action: DynamicFieldAction): void {
    switch (action) {
      case DynamicFieldAction.Update:
        this.showManualAddress = true;
    }

    this.cdr.markForCheck();
  }

  private onGlobalValidityCheck(): void {
    this.internalForm.markAllAsTouched();

    forEach(this.internalForm.controls, (control) => {
      control.updateValueAndValidity({ emitEvent: false });
    });

    this.showManualAddress = this.internalForm.invalid;
  }

  private scrollToForm(): void {
    this.viewportScroller.setOffset([0, 20]);

    setTimeout(() =>
      this.viewportScroller.scrollToAnchor(this.field.key as string)
    );
  }
}
